/**
 * Renderer wrappers over `window.electronAPI.azureStorage` (the Task 1.2
 * `AzureStorageApi` surface). Every IPC answer is a `StorageIpcResult<T>`;
 * these helpers unwrap it and THROW the typed `StorageOperationError` so
 * TanStack Query carries it as `error` (no SQL error strings, spec §9).
 * Never call the SDK, never touch a query path.
 */
import type {
  AzureStorageApi,
  AzureStorageIpcRequest,
  StorageAccountInfo,
  StorageDownloadRequest,
  StorageDownloadResult,
  StorageIpcResult,
  StorageItem,
  StorageOperationError,
  StoragePageRequest,
  StoragePageResult,
  StorageResourceProperties,
  StorageContainerAcl,
  StorageResourceAcl,
  StorageSasGrant,
  StorageSasMintedEvent,
  StorageWriteOutcome,
  StorageEntity,
  StorageEntityGetRequest,
  StorageEntityQueryRequest,
  StorageEntityWriteRequest,
  StorageEntityWriteResult,
  StorageEntityExportRequest,
  StorageTableAdminRequest,
  StorageFileItemProperties,
  StorageShareProtocolAnswer,
  StorageContentReadRequest,
  StorageContentReadResult,
  StorageContentWriteRequest,
  StorageContentWriteResult,
} from '../../../../types/azureStorage';

export const STORAGE_API_UNAVAILABLE: StorageOperationError = {
  category: 'unsupported',
  code: 'ApiUnavailable',
  retryable: false,
  message: 'Azure Storage is not available in this build.',
};

export function getAzureStorageApi(): AzureStorageApi | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.electronAPI?.azureStorage;
}

export function isStorageOperationError(value: unknown): value is StorageOperationError {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return typeof record.category === 'string' && typeof record.message === 'string' && typeof record.retryable === 'boolean';
}

/** Normalise anything a listing may reject with into the typed error the UI renders. */
export function toStorageOperationError(value: unknown): StorageOperationError {
  if (isStorageOperationError(value)) return value;
  const message = value instanceof Error ? value.message : typeof value === 'string' ? value : 'Unknown storage error';
  return { category: 'unknown', retryable: false, message };
}

export function unwrapStorageResult<T>(result: StorageIpcResult<T>): T {
  if ('error' in result) throw result.error;
  return result.value;
}

function requireApi(): AzureStorageApi {
  const api = getAzureStorageApi();
  if (!api) throw STORAGE_API_UNAVAILABLE;
  return api;
}

let requestCounter = 0;
/** Unique per renderer process; the main process keys cancellation and late-result discard on it. */
export function newStorageRequestId(): string {
  requestCounter += 1;
  return `req-${Date.now().toString(36)}-${requestCounter.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** A Save's `mutationBatch` id: unique across windows too (S4 review H, R1), since the backend keys batches by it. */
export const newStorageMutationBatchId = (): string => crypto.randomUUID();

export async function listStoragePage(request: StoragePageRequest): Promise<StoragePageResult<StorageItem>> {
  return unwrapStorageResult(await requireApi().list(request));
}

/** Best-effort: a cancel that fails (handler gone, request already finished) is never an error the UI shows. */
export async function cancelStorageRequest(requestId: string): Promise<void> {
  const api = getAzureStorageApi();
  if (!api) return;
  try {
    await api.cancel({ requestId });
  } catch {
    /* best-effort */
  }
}

/** Best-effort cursor release (spec §8.1: cursors are main-process owned and bounded). */
export async function releaseStorageCursor(connectionId: string, cursorHandle: string): Promise<void> {
  const api = getAzureStorageApi();
  if (!api) return;
  try {
    await api.cursorRelease({ connectionId, cursorHandle });
  } catch {
    /* best-effort */
  }
}

export async function downloadStorageResources(request: StorageDownloadRequest): Promise<StorageDownloadResult> {
  return unwrapStorageResult(await requireApi().download(request));
}

export async function getStorageResourceProperties(request: AzureStorageIpcRequest<'azure-storage:get-properties'>): Promise<StorageResourceProperties> {
  return unwrapStorageResult(await requireApi().getProperties(request));
}

export async function setStorageMetadata(request: AzureStorageIpcRequest<'azure-storage:set-metadata'>): Promise<StorageWriteOutcome<StorageResourceProperties>> {
  return unwrapStorageResult(await requireApi().setMetadata(request));
}

export async function setStorageBlobHeaders(request: AzureStorageIpcRequest<'azure-storage:set-headers'>): Promise<StorageWriteOutcome<StorageResourceProperties>> {
  return unwrapStorageResult(await requireApi().setHeaders(request));
}

export async function setStorageBlobTags(request: AzureStorageIpcRequest<'azure-storage:set-tags'>): Promise<StorageWriteOutcome<StorageResourceProperties>> {
  return unwrapStorageResult(await requireApi().setTags(request));
}

export async function setStorageBlobAccessTier(request: AzureStorageIpcRequest<'azure-storage:set-access-tier'>): Promise<StorageWriteOutcome<{ tier: string; archiveStatus?: string; rehydratePriority?: string }>> {
  return unwrapStorageResult(await requireApi().setAccessTier(request));
}

export async function getStorageContainerAcl(request: AzureStorageIpcRequest<'azure-storage:get-container-acl'>): Promise<StorageContainerAcl> {
  return unwrapStorageResult(await requireApi().getContainerAcl(request));
}

export async function setStorageContainerAcl(request: AzureStorageIpcRequest<'azure-storage:set-container-acl'>): Promise<StorageWriteOutcome<StorageContainerAcl>> {
  return unwrapStorageResult(await requireApi().setContainerAcl(request));
}

/** Table / queue stored access policies (follow-up 2026-09-25). */
export async function getStorageAccessPolicies(request: AzureStorageIpcRequest<'azure-storage:get-access-policies'>): Promise<StorageResourceAcl> {
  return unwrapStorageResult(await requireApi().getAccessPolicies(request));
}

export async function setStorageAccessPolicies(request: AzureStorageIpcRequest<'azure-storage:set-access-policies'>): Promise<StorageWriteOutcome<StorageResourceAcl>> {
  return unwrapStorageResult(await requireApi().setAccessPolicies(request));
}

export async function generateStorageSas(request: AzureStorageIpcRequest<'azure-storage:generate-sas'>): Promise<StorageSasGrant> {
  return unwrapStorageResult(await requireApi().generateSas(request));
}

export function onStorageSasMinted(callback: (event: StorageSasMintedEvent) => void): () => void {
  return requireApi().onSasMinted(callback);
}

export async function getStorageAccountInfo(connectionId: string): Promise<StorageAccountInfo> {
  return unwrapStorageResult(await requireApi().accountInfo({ connectionId }));
}

export async function queryStorageEntities(request: StorageEntityQueryRequest): Promise<StoragePageResult<StorageEntity>> {
  return unwrapStorageResult(await requireApi().queryEntities(request));
}

export async function getStorageEntity(request: StorageEntityGetRequest): Promise<StorageEntity> {
  return unwrapStorageResult(await requireApi().getEntity(request));
}

export async function writeStorageEntities(request: StorageEntityWriteRequest): Promise<StorageEntityWriteResult> {
  return unwrapStorageResult(await requireApi().writeEntities(request));
}

export async function exportStorageEntities(request: StorageEntityExportRequest): Promise<{ started: true; jobId: string }> {
  return unwrapStorageResult(await requireApi().exportEntities(request));
}

export async function createStorageTable(request: StorageTableAdminRequest): Promise<{ table: string }> {
  return unwrapStorageResult(await requireApi().createTable(request));
}

export async function deleteStorageTable(request: StorageTableAdminRequest): Promise<{ table: string }> {
  return unwrapStorageResult(await requireApi().deleteTable(request));
}

// S7 begin — file-share channels (plan §3 IPC table); the share-management dialogs (Task 7.16) keep their own wrappers.
export async function getShareProtocol(request: AzureStorageIpcRequest<'azure-storage:share-protocol'>): Promise<StorageShareProtocolAnswer> {
  return unwrapStorageResult(await requireApi().shareProtocol(request));
}

export async function getFileItemProperties(request: AzureStorageIpcRequest<'azure-storage:file-properties'>): Promise<StorageFileItemProperties> {
  return unwrapStorageResult(await requireApi().fileProperties(request));
}

export async function downloadFileResources(request: AzureStorageIpcRequest<'azure-storage:file-download'>): Promise<StorageDownloadResult> {
  return unwrapStorageResult(await requireApi().fileDownload(request));
}

export async function uploadFileResources(request: AzureStorageIpcRequest<'azure-storage:file-upload'>): Promise<StorageDownloadResult> {
  return unwrapStorageResult(await requireApi().fileUpload(request));
}

export async function deleteFileResources(request: AzureStorageIpcRequest<'azure-storage:file-delete'>): Promise<StorageDownloadResult> {
  return unwrapStorageResult(await requireApi().fileDelete(request));
}

export async function createShareDirectory(request: AzureStorageIpcRequest<'azure-storage:directory-create'>): Promise<void> {
  unwrapStorageResult(await requireApi().directoryCreate(request));
}
// S7 end

/**
 * Preview / edit pane: one object's first bytes in memory (`requestId` from `newStorageRequestId()` — `cancelStorageRequest`
 * with it aborts the read). The bytes arrive as a `Uint8Array`.
 */
export async function readStorageContent(request: StorageContentReadRequest): Promise<StorageContentReadResult> {
  return unwrapStorageResult(await requireApi().readContent(request));
}

/** Saves edited bytes (overwrite at the loaded ETag, overwrite anyway, or a new copy); answers the re-read facts. */
export async function writeStorageContent(request: StorageContentWriteRequest): Promise<StorageContentWriteResult> {
  return unwrapStorageResult(await requireApi().writeContent(request));
}
